import type { CoverSheetConfigCard, Layout } from '../../types';
import { yearBuiltField } from './complianceUpstreamFields';

type LayoutRows = (Layout[] | null)[];

const isYearBuiltRow = (row: Layout[] | null) =>
  !!row?.some((item) => item?.name === 'property.yearBuilt');

const besideYearBuilt =
  (layoutItem: Layout, position: 'before' | 'after') =>
  (layout: LayoutRows): LayoutRows => {
    if (!layout.some(isYearBuiltRow)) {
      return layout.concat([[layoutItem]]);
    }

    return layout.map((row) => {
      if (!isYearBuiltRow(row)) return row;
      return position === 'before'
        ? [layoutItem, ...(row ?? [])]
        : [...(row ?? []), layoutItem];
    });
  };

/**
 * Puts APN Number next to Year Built.
 */
export const withApnBesideYearBuilt = besideYearBuilt(
  { name: 'property.assessorParcelNumber', size: 50, mobileSize: 100 },
  'before',
);

/**
 * Puts Property Tax ID Number next to Year Built.
 */
export const withTaxIdBesideYearBuilt = besideYearBuilt(
  { name: 'property.taxIdNumber', size: 50, mobileSize: 100 },
  'after',
);

export const propertyBeingSoldCard = {
  id: 'propertyInfo.propertyBeingSold',
  innerTitle: 'Property Being Sold',
  legend: '* indicates a required field',
  layout: [
    [
      {
        name: 'property.street1',
        size: 75,
      },
      {
        name: 'property.street2',
        size: 25,
      },
    ],
    [
      {
        name: 'property.city',
        size: 50,
      },
      {
        name: 'property.state',
        size: 25,
        mobileSize: 50,
      },
      {
        name: 'property.zip',
        size: 25,
        mobileSize: 50,
      },
    ],
    [
      {
        name: 'property.county',
        size: 100,
      },
    ],
    [{ name: 'property.yearBuilt', size: 50, mobileSize: 100 }],
  ],
  fields: {
    street1: {
      name: 'property.street1',
      entity: 'property',
      type: 'autofill',
      label: 'Street',
      placeholderText: '',
      action: {
        name: 'autofill',
        provider: 'googlePlacesSearch',
        label: {
          primary: 'label.primary',
          secondary: 'label.secondary',
        },
        constraints: ['limitToLicensedStates', 'limitToInteractionState'],
        props: [
          {
            from: 'street1',
            to: 'property.street1',
          },
          {
            from: 'street2',
            to: 'property.street2',
          },
          {
            from: 'city',
            to: 'property.city',
          },
          {
            from: 'county',
            to: 'property.county',
          },
          {
            from: 'zip',
            to: 'property.zip',
          },
        ],
      },
      validation: [
        {
          type: 'required',
          message: 'Required',
        },
      ],
    },
    street2: {
      name: 'property.street2',
      entity: 'property',
      type: 'text',
      label: 'Apt/Unit/Suite Number',
      placeholderText: '',
    },
    city: {
      name: 'property.city',
      entity: 'property',
      type: 'text',
      label: 'City',
      placeholderText: '',
      validation: [
        {
          type: 'required',
          message: 'Required',
        },
      ],
    },
    state: {
      name: 'property.state',
      entity: 'property',
      type: 'state',
      label: 'State',
      placeholderText: '',
      disabled: true,
      validation: [
        {
          type: 'required',
          message: 'Required',
        },
      ],
    },
    zip: {
      name: 'property.zip',
      entity: 'property',
      type: 'zip',
      label: 'Zip Code',
      maxLength: 10,
      placeholderText: '',
      validation: [
        {
          type: 'required',
          message: 'Required',
        },
        {
          type: 'pattern',
          value:
            '^(([0-9]{5}(-[0-9]{4})?)|([A-Z][0-9][A-Z] [0-9][A-Z][0-9]))?$',
          message: 'Invalid Zip Code',
        },
      ],
    },
    county: {
      name: 'property.county',
      entity: 'property',
      type: 'text',
      label: 'County',
      placeholderText: '',
      validation: [
        {
          type: 'required',
          message: 'Required',
        },
      ],
    },
    yearBuilt: yearBuiltField,
  },
} satisfies CoverSheetConfigCard;
